/**
 * F17 T17.11.5 — modal "Nowy wzór procesu" wywoływany z listy wzorów (admin scope).
 *
 * Pola: nazwa (required, max 200), slug (required, max 100, [a-z0-9_-]),
 * opis (optional), targetModuleCode (z whitelisty SUPPORTED_PROCESS_TARGET_MODULES).
 *
 * Po sukcessie: callback onCreated(templateId) — caller navigate do buildera.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_PROCESS_TARGET_MODULES } from '@saas/shared';
import { useMutation } from '@tanstack/react-query';

import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { createAdminProcessTemplate } from '@/lib/admin-process-templates/api';

export interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (templateId: string) => void;
}

const SLUG_REGEX = /^[a-z0-9][a-z0-9_-]{0,99}$/;

export function CreateTemplateModal({ open, onClose, onCreated }: CreateTemplateModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [targetModuleCode, setTargetModuleCode] = useState<string>(
    SUPPORTED_PROCESS_TARGET_MODULES[0] ?? '',
  );

  // Reset po zamknięciu
  useEffect(() => {
    if (!open) {
      setName('');
      setSlug('');
      setDescription('');
      setTargetModuleCode(SUPPORTED_PROCESS_TARGET_MODULES[0] ?? '');
    }
  }, [open]);

  // Auto-slug z nazwy (lowercase, zamień spacje/znaki niedozwolone → '-')
  function handleNameChange(value: string) {
    setName(value);
    if (slug === '') {
      const auto = value
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9_-]/g, '')
        .slice(0, 100);
      setSlug(auto);
    }
  }

  const mutation = useMutation({
    mutationFn: () =>
      createAdminProcessTemplate({
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        targetModuleCode,
      }),
    onSuccess: (tpl) => {
      toast.show({ variant: 'success', title: t('admin.processTemplates.toast.createSuccess') });
      onCreated(tpl.id);
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('admin.processTemplates.toast.createError') });
    },
  });

  const nameInvalid = name.trim().length === 0 || name.trim().length > 200;
  const slugInvalid = slug.trim().length === 0 || !SLUG_REGEX.test(slug.trim());
  const canSubmit = !nameInvalid && !slugInvalid && targetModuleCode !== '' && !mutation.isPending;

  function handleSubmit() {
    if (!canSubmit) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.processTemplates.createModal.title')}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-lg border border-border-secondary px-4 py-2 text-sm text-text-primary hover:bg-bg-secondary disabled:opacity-50"
            data-testid="create-template-cancel-btn"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-accent-600 px-4 py-2 text-sm text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="create-template-submit-btn"
          >
            {mutation.isPending
              ? t('common.saving')
              : t('admin.processTemplates.createModal.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Nazwa */}
        <div>
          <label
            htmlFor="create-template-name"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('admin.processTemplates.createModal.nameLabel')}
            <span className="ml-1 text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <Input
            id="create-template-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={200}
            placeholder={t('admin.processTemplates.createModal.namePlaceholder')}
            data-testid="create-template-name-input"
          />
        </div>

        {/* Slug */}
        <div>
          <label
            htmlFor="create-template-slug"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('admin.processTemplates.createModal.slugLabel')}
            <span className="ml-1 text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <Input
            id="create-template-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            maxLength={100}
            placeholder={t('admin.processTemplates.createModal.slugPlaceholder')}
            className="font-mono"
            data-testid="create-template-slug-input"
          />
          <p className="mt-1 text-xs text-text-secondary">
            {t('admin.processTemplates.createModal.slugHint')}
          </p>
          {slugInvalid && slug.length > 0 && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {t('admin.processTemplates.createModal.slugError')}
            </p>
          )}
        </div>

        {/* Moduł docelowy */}
        <div>
          <label
            htmlFor="create-template-module"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('admin.processTemplates.createModal.moduleLabel')}
            <span className="ml-1 text-red-500" aria-hidden="true">
              *
            </span>
          </label>
          <Dropdown
            id="create-template-module"
            value={targetModuleCode}
            onChange={setTargetModuleCode}
            className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
            data-testid="create-template-module-select"
            options={SUPPORTED_PROCESS_TARGET_MODULES.map((code) => ({
              value: code,
              label: t(`tenant.processes.list.createModal.modules.${code.replace(/\./g, '_')}`),
            }))}
          />
        </div>

        {/* Opis */}
        <div>
          <label
            htmlFor="create-template-description"
            className="mb-1 block text-sm font-medium text-text-primary"
          >
            {t('admin.processTemplates.createModal.descriptionLabel')}
          </label>
          <textarea
            id="create-template-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={t('admin.processTemplates.createModal.descriptionPlaceholder')}
            className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
            data-testid="create-template-description-input"
          />
        </div>
      </div>
    </Modal>
  );
}
